import React from 'react';
import { useMyCalls } from '../logic/useMyCalls';
import { useMemberGroups } from '../logic/useMemberGroups';
import { useAppStore } from '../../../store/useAppStore';
import { Permission } from '../../auth/types';
import { Icon } from '../../../components/common/Icon';
import { formatScheduleTimeRangeForDisplay } from '../../scheduler/time-format';

export const MyCallWidget: React.FC = () => {
    const user = useAppStore(state => state.auth.user);
    const cloudMember = useAppStore(state => state.cloud.member);
    const roles = useAppStore(state => state.admin.roles);
    
    // Get user permissions
    const userRole = cloudMember ? roles.find(r => r.id === cloudMember.roleId) : null;
    const permissions = user?.isSuperAdmin 
        ? Object.values(Permission) 
        : (userRole?.permissions || []);
    
    const userGroups = useMemberGroups(permissions);
    const myCalls = useMyCalls(user?.uid || null, userGroups);

    if (!user) {
        return (
            <div className="bg-white dark:bg-surface-dark rounded-xl border border-gray-200 dark:border-border-dark p-6">
                <p className="text-gray-500 dark:text-gray-400 text-center">Sign in to view your calls</p>
            </div>
        );
    }

    if (myCalls.length === 0) {
        return (
            <div className="bg-white dark:bg-surface-dark rounded-xl border border-gray-200 dark:border-border-dark p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                    <Icon name="event" className="text-primary" />
                    My Call
                </h3>
                <div className="text-center py-8">
                    <Icon name="event_available" size="xl" className="text-gray-300 dark:text-gray-600 mb-2" />
                    <p className="text-gray-500 dark:text-gray-400">No upcoming calls in the next 48 hours</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white dark:bg-surface-dark rounded-xl border border-gray-200 dark:border-border-dark p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                <Icon name="event" className="text-primary" />
                My Call
            </h3>
            <div className="space-y-3">
                {myCalls.map(call => (
                    <div 
                        key={call.id}
                        className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                    >
                        <div className="flex items-start justify-between">
                            <div className="flex-1">
                                <h4 className="font-medium text-gray-900 dark:text-gray-100">{call.title}</h4>
                                <div className="mt-1 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                                    <div className="flex items-center gap-2">
                                        <Icon name="schedule" size="sm" />
                                        {call.date} • {formatScheduleTimeRangeForDisplay(call.startTime, call.endTime)}
                                    </div>
                                    {call.location ? <div className="flex items-center gap-2">
                                            <Icon name="location_on" size="sm" />
                                            {call.location}
                                        </div> : null}
                                </div>
                            </div>
                            <span className={`px-2 py-1 text-xs font-medium rounded ${
                                call.type === 'performance' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300' :
                                call.type === 'tech' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300' :
                                'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                            }`}>
                                {call.type}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
